import { Plus } from 'lucide-react';
import { Skeleton } from '@/components/ui';
import { Button } from '..';
import { cn } from '@/lib';

export const RespondersListSkeleton = () => {
  return (
    <div className="flex w-full h-full flex-col bg">
      {/* Tabs and Searchbar Skeleton */}
      <div className="flex flex-col justify-between p-3 gap-2">
        <div className="flex gap-2">
          <Skeleton className="h-8 w-20 rounded-md bg-strong" />
          <Skeleton className="h-8 w-20 rounded-md bg-strong" />
        </div>
        <Skeleton className="h-9 w-full rounded-md bg-strong" />
      </div>

      {/* Rows Skeleton */}
      <div className="flex-1 overflow-y-auto px-3">
        <div className="grid gap-2 text-xs">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="flex flex-row justify-between p-3 border border-border rounded-xl shadow-sm bg animate-pulse"
            >
              {/* Left side: avatar, name, presence, incident, badge */}
              <div className="flex flex-col gap-3 flex-1">
                {/* Avatar + Name + Speaking Indicator */}
                <div className="flex flex-row items-center gap-2">
                  <div className="relative">
                    <Skeleton className="h-8 w-8 rounded-full bg-strong" />
                    {/* Presence Dot */}
                    <div className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-muted border-2 border-onColor" />
                  </div>
                  <div className="flex flex-col gap-1 flex-1">
                    <Skeleton className="h-3 w-full max-w-[120px] bg-strong" />
                    <Skeleton className="h-3 w-full max-w-[60px] bg-strong" />
                  </div>
                </div>

                {/* Incident Name + Badge */}
                <div className="flex flex-col gap-1">
                  <Skeleton className="h-3 w-full max-w-[150px] bg-strong" />
                  <Skeleton className="h-4 w-16 rounded-full bg-strong" />
                </div>
              </div>

              {/* Right side: PTV + PTT Icons */}
              <div className="flex flex-col justify-between gap-2">
                <Skeleton className="h-8 w-8 rounded-md bg-strong" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ChatSkeleton = () => (
  <div className="p-3 bg flex w-full h-full">
    <div className="bg flex flex-row border border-border rounded-lg w-full min-w-[37.75rem] h-full overflow-hidden">
      {/* Chat Sidebar Skeleton */}
      <div className="flex w-1/3 border-r border-border overflow-hidden">
        <div className="flex flex-col w-full h-full overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            {/* Search bar skeleton */}
            <div className="p-4 flex items-center justify-center">
              <Skeleton className="h-16 bg-strong w-full rounded-lg" />
            </div>
            <hr className="w-full flex" />

            {/* Chat list skeletons */}
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 rounded-lg">
                <Skeleton className="h-12 w-12 bg-strong rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4 bg-strong rounded" />
                  <Skeleton className="h-3 w-1/2 bg-strong rounded" />
                </div>
                <Skeleton className="h-3 w-10 bg-strong rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chat Window Skeleton */}
      <div className="flex flex-1 flex-col gap-2 items-center justify-center overflow-hidden">
        <div className="p-3 w-full flex items-center justify-center">
          <Skeleton className="flex bg-strong rounded-lg w-48 h-48" />
        </div>
        <Skeleton className="flex bg-strong rounded-lg w-72 h-8" />
        <Skeleton className="flex bg-strong rounded-lg w-80  h-6" />
      </div>
    </div>
  </div>
);

// Individual Card Skeleton (represents one row)
const IncidentCardSkeleton = () => {
  return (
    <div className="flex items-center justify-between gap-3 p-4 rounded-lg border">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex-1 space-y-2">
          <Skeleton className="h-4 w-20 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
};

// Full Table Skeleton
export const IncidentTableSkeleton = () => {
  return (
    <div className="flex flex-col h-full overflow-hidden bg">
      {/* Header section */}
      <div className="flex flex-row justify-between p-3 gap-2">
        {/* Tabs skeletons */}
        <div className="flex flex-row gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-20 rounded-md bg-muted animate-pulse" />
          ))}
        </div>

        {/* Add button */}
        <Button className="p-3" variant="default" size="icon" disabled>
          <Plus size={'1rem'} />
        </Button>
      </div>

      {/* Searchbar skeleton */}
      <div className="px-3 pb-3">
        <Skeleton className="h-9 w-full rounded-md bg-muted animate-pulse" />
      </div>

      {/* Table area */}
      <div className="min-w-[30rem] flex flex-col flex-1 overflow-hidden overflow-x-auto">
        {/* Column headers */}
        <div className="grid grid-cols-6 items-center text-xs font-normal text-muted px-6 py-3 border-t border-b">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-24 rounded animate-pulse" />
          ))}
        </div>

        {/* Body skeleton rows */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-2 p-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <IncidentCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Skeleton for display name
export const DisplayNameSkeleton = () => (
  <div className="flex flex-col items-start">
    <div className="h-6 bg-muted rounded animate-pulse w-32 mb-1" />
    <div className="h-4 bg-muted rounded animate-pulse w-24" />
  </div>
);

// Skeleton loader component
export const ChatSidebarSkeleton = () => (
  <div className="p-2 h-14 cursor-pointer flex justify-between items-center border-b">
    <div className="flex items-center justify-between w-full gap-2">
      {/* Left side - Avatar and chat info skeleton */}
      <div className="flex items-center gap-2 min-w-0 max-w-72 flex-1">
        {/* Avatar skeleton */}
        <div className="w-8 h-8 rounded-full bg-muted animate-pulse flex-shrink-0" />

        {/* Text skeleton */}
        <div className="flex flex-col justify-center min-w-0 flex-1 gap-1">
          <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
          <div className="h-3 bg-muted rounded animate-pulse w-1/2" />
        </div>
      </div>

      {/* Right side - Time and icons skeleton */}
      <div className="flex flex-col items-end justify-center flex-shrink-0 gap-1">
        <div className="h-3 bg-muted rounded animate-pulse w-10" />
        <div className="flex gap-1">
          <div className="w-4 h-4 bg-muted rounded animate-pulse" />
          <div className="w-4 h-4 bg-muted rounded animate-pulse" />
          <div className="w-6 h-4 bg-muted rounded animate-pulse" />
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for search results
export const SearchResultSkeleton = () => (
  <div className="p-4 border-b">
    <div className="h-4 bg-muted rounded animate-pulse w-16 mb-2" />
    <div className="space-y-2">
      <div className="h-4 bg-muted rounded animate-pulse w-full" />
      <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
    </div>
  </div>
);

// Skeleton loader for individual messages that are being translated
export const MessageSkeleton = ({ isOwnMessage = false }) => (
  <div className={cn('flex w-full mt-4 max-w-xs', isOwnMessage && 'ml-auto justify-end')}>
    {/* Only show avatar for other people's messages (on the left) */}
    {!isOwnMessage && <div className="flex-shrink-0 h-8 w-8 rounded-full bg-muted animate-pulse mr-3" />}

    <div className={cn('flex flex-col space-y-2', isOwnMessage ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'rounded-lg px-4 py-2',
          isOwnMessage ? 'bg-primary text-inverse rounded-br-none' : 'bg-elevated rounded-bl-none'
        )}
      >
        <div className="flex flex-col space-y-2">
          <div className="h-4 bg-muted rounded animate-pulse w-48" />
          <div className="h-4 bg-muted rounded animate-pulse w-32" />
        </div>
      </div>
      <div className="h-3 bg-muted rounded animate-pulse w-16" />
    </div>
  </div>
);
